import { Cancel, MagnifyingGlass } from '@1debit/icons';
import { forwardRef, useState } from 'react';

import { getSearchInputStyles } from './helpers';
import { type BaseInputProps } from './InputController';

import { useForwardedRef } from '../../hooks/useForwardedRef';
import { Button } from '../../server/Button/Button';
import { classNames } from '../../server/utils/classNames';
import { type AnalyticsId } from '../AnalyticsProvider/AnalyticsProvider';

type CustomProps = {
	/** This property is only used as label's htmlFor and input's id attributes to enhance acessibility.  */
	label: string;
	onChange: React.ChangeEventHandler<HTMLInputElement>;
	clearButtonAnalyticsId: AnalyticsId;
	variant?: 'field' | 'bar';
};

export type SearchBarProps = Omit<BaseInputProps<HTMLInputElement>, 'type' | 'onChange'> & CustomProps;

export const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(
	(
		{
			className,
			label,
			disabled,
			clearButtonAnalyticsId,
			placeholder = 'Start typing to search',
			variant = 'field',
			onChange,
			...rest
		},
		ref,
	) => {
		const forwardedRef = useForwardedRef(ref);
		const [showClearButton, setShowClearButton] = useState(!!rest.value || !!rest.defaultValue);

		const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			setShowClearButton(!!e.target.value);
			onChange(e);
		};

		const inputClasses = classNames(getSearchInputStyles(disabled), className);

		const containerClasses = classNames(
			'w-full h-14 relative bg-bg-primary flex flex-row items-center px-4 py-3 border-border-primary transition-shadow ring-transparent text-content-secondary',
			{
				'border-2 ring-[6px] rounded-lg focus-within:border-content-primary focus-within:ring-bg-tertiary hover:ring-bg-tertiary':
					variant === 'field',
				'border-y': variant === 'bar',
				'cursor-not-allowed hover:ring-transparent text-content-disabled-primary': disabled,
				'bg-accent-grey-100': variant === 'field' && disabled,
			},
		);

		return (
			<label htmlFor={label} className="w-full">
				<div className={containerClasses}>
					<MagnifyingGlass />
					<input
						disabled={disabled}
						placeholder={placeholder}
						className={inputClasses}
						onChange={handleOnChange}
						ref={forwardedRef}
						type="search"
						id={label}
						{...rest}
					/>
					{showClearButton && (
						<Button
							analyticsId={clearButtonAnalyticsId}
							variant="link"
							aria-label="Clear search"
							className={classNames('active:bg-transparent text-content-primary', {
								'text-content-disabled-primary': disabled,
							})}
							disabled={disabled}
							onClick={e => {
								forwardedRef.current?.focus();
								if (forwardedRef.current) forwardedRef.current.value = '';
								// @ts-expect-error TS2322
								handleOnChange({ ...e, target: { ...e.target, value: '' } });
							}}>
							<Cancel />
						</Button>
					)}
				</div>
			</label>
		);
	},
);
